import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  connect() {
    this.scrollThreshold = 150; // Define your scroll threshold here

    // Initial check for scroll state and menu state on page load
    this.updateBodyScrollAndNavClasses();

    // Listen for scroll events
    this.scrollListener = this.updateBodyScrollAndNavClasses.bind(this);
    window.addEventListener('scroll', this.scrollListener);

    // Observe class changes on the header element (this.element)
    // This helps react when AlpineJS toggles the 'bg-white' class for the mobile menu
    this.classObserver = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          // Class attribute changed, re-evaluate scroll and menu states
          this.updateBodyScrollAndNavClasses();
          break; // No need to check other mutations if class already changed
        }
      }
    });
    this.classObserver.observe(this.element, { attributes: true });

    // Add event listeners to menu links
    this.menuLinks = this.element.querySelectorAll('a');
    this.boundCloseMenu = this.closeMenuAndDispatchEvents.bind(this);
    this.menuLinks.forEach(link => {
      link.addEventListener('click', this.boundCloseMenu);
    });
  }

  disconnect() {
    window.removeEventListener('scroll', this.scrollListener);
    if (this.classObserver) {
      this.classObserver.disconnect();
    }
    // Ensure body scroll is re-enabled if the controller is disconnected while menu is open
    document.body.classList.remove('overflow-hidden');

    // Remove event listeners from menu links
    if (this.menuLinks) {
      this.menuLinks.forEach(link => {
        link.removeEventListener('click', this.boundCloseMenu);
      });
    }
  }

  closeMenuAndDispatchEvents() {
    // Dispatch an event that AlpineJS should listen for to close the menu.
    // AlpineJS is responsible for changing its 'open' state and updating classes
    // accordingly (e.g., removing 'bg-white').
    // The MutationObserver in this controller will then detect the class change
    // (once AlpineJS makes it) and call updateBodyScrollAndNavClasses.
    // We dispatch unconditionally; Alpine can decide if its state needs to change.
    const event = new CustomEvent('close-mobile-menu', { bubbles: true });
    this.element.dispatchEvent(event);
  }

  updateBodyScrollAndNavClasses() {
    const nav = this.element;
    const isMobileMenuExpanded = nav.classList.contains('bg-white');

    if (isMobileMenuExpanded) {
      // Mobile menu is open
      // Prevent body scrolling
      if (!document.body.classList.contains('overflow-hidden')) {
        document.body.classList.add('overflow-hidden');
      }

      // Ensure 'scrolled' class is REMOVED from nav to allow 'bg-white' to take effect.
      if (nav.classList.contains('scrolled')) {
        nav.classList.remove('scrolled');
      }
    } else {
      // Mobile menu is closed
      // Allow body scrolling
      if (document.body.classList.contains('overflow-hidden')) {
        document.body.classList.remove('overflow-hidden');
      }

      // Apply normal scroll-based class changes to nav
      if (window.pageYOffset > this.scrollThreshold) {
        if (!nav.classList.contains('scrolled')) {
          nav.classList.add('scrolled');
        }
      } else {
        if (nav.classList.contains('scrolled')) {
          nav.classList.remove('scrolled');
        }
      }
    }
  }
}
